// src/Vitrina.tsx
import { useCallback as useCallback2, useEffect as useEffect2, useMemo as useMemo3, useReducer as useReducer2, useRef as useRef3, useState as useState2 } from "react";
import { gsap as gsap4 } from "gsap";

// src/defaults.ts
var DEFAULT_LAYOUT = {
  world: { w: 4645, h: 3044 },
  compactWorld: { w: 2200, h: 3e3 },
  compactBreakpoint: 640,
  count: 114,
  columns: 14,
  baseSize: 190,
  sizeJitter: 0.15,
  minSeparation: 2,
  seed: "vitrina",
  compactSizeFactor: 0.62
};
function resolveLayout(layout) {
  const given = {};
  for (const [key, value] of Object.entries(layout ?? {})) {
    if (value !== void 0) given[key] = value;
  }
  return { ...DEFAULT_LAYOUT, ...given };
}
var DEFAULT_ZOOM_STEPS = [0.75, 1, 1.25];
var DEFAULT_ZOOM_INDEX = 1;
var EDGE_RESISTANCE = 0.8;
var WHEEL_SPEED = 1.6;
var DRAG_THRESHOLD_PX = 5;
var INTRO_SCALE = 0.5;
var REVEAL_SCALE = 0.6;
var REVEAL_GAP_MS = [30, 80];
var REVEAL_INSET = 0.12;
var WHEEL_CHASE_SECONDS = 0.16;
var WHEEL_CHASE_EASE = "power3.out";
var ZOOM_TWEEN_SECONDS = 0.32;
var ZOOM_TWEEN_EASE = "power2.out";
var REVEAL_POP_SECONDS = 0.45;
var INTRO_POP_SECONDS = 0.7;
var REVEAL_POP_EASE = "back.out(1.4)";
var VIEW_FLIP_SECONDS = 0.7;
var DETAIL_FLIGHT_SECONDS = 0.6;
var DETAIL_FLIGHT_EASE = "power3.inOut";
var DETAIL_PANEL_EASE = "power3.inOut";
var DETAIL_PANEL_SECONDS = 0.45;
var DETAIL_LINE_SHIFT = 12;
var DETAIL_LINE_EASE = "power3.out";
var DETAIL_LINE_EXIT_EASE = "power3.in";
var DETAIL_LINE_STAGGER_SECONDS = 0.07;
var DETAIL_LINE_STAGGER_EXIT_SECONDS = 0.04;

// src/context.ts
import { createContext, useContext } from "react";
var VitrinaContext = createContext(null);
function useVitrina() {
  const api = useContext(VitrinaContext);
  if (api === null) {
    throw new Error("useVitrina() must be called from a descendant of <Vitrina> \u2014 pass controls as its children");
  }
  return api;
}

// src/gsap.ts
import { useEffect, useLayoutEffect } from "react";
import { gsap } from "gsap";
var pluginsPromise = null;
var plugins = null;
var getInteractionPlugins = () => plugins;
function loadInteractionPlugins() {
  pluginsPromise ?? (pluginsPromise = Promise.all([
    import("gsap/Draggable"),
    import("gsap/InertiaPlugin"),
    import("gsap/Observer"),
    import("gsap/Flip")
  ]).then(([draggable, inertia, observer, flip]) => {
    gsap.registerPlugin(
      draggable.Draggable,
      inertia.InertiaPlugin,
      observer.Observer,
      flip.Flip
    );
    plugins = { Draggable: draggable.Draggable, Observer: observer.Observer, Flip: flip.Flip };
    return plugins;
  }));
  return pluginsPromise;
}
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
function revertContext(ctx) {
  ctx?.revert({ suppressEvents: true });
}
function addDetached(ctx, fn) {
  const active = gsap.context();
  ctx.add(fn);
  if (!active || active === ctx) return;
  const data = active.data;
  for (let i = data.length - 1; i >= 0; i--) if (data[i] === ctx) data.splice(i, 1);
}
function useGsapContext(setup, scopeRef, deps) {
  useIsomorphicLayoutEffect(() => {
    const ctx = gsap.context((self) => setup(self), scopeRef.current ?? void 0);
    return () => revertContext(ctx);
  }, deps);
}

// src/motion.ts
var FALLBACK_MOTION = {
  durMicro: WHEEL_CHASE_SECONDS,
  durUi: ZOOM_TWEEN_SECONDS,
  durFlight: DETAIL_FLIGHT_SECONDS,
  durPanel: DETAIL_PANEL_SECONDS,
  easeMicro: WHEEL_CHASE_EASE,
  easeFlight: DETAIL_FLIGHT_EASE,
  staggerLine: DETAIL_LINE_STAGGER_SECONDS,
  staggerLineExit: DETAIL_LINE_STAGGER_EXIT_SECONDS
};
function seconds(style, name, fallback) {
  const raw = style.getPropertyValue(name).trim();
  if (!raw) return fallback;
  const value = parseFloat(raw);
  if (Number.isNaN(value)) return fallback;
  return raw.endsWith("ms") ? value / 1e3 : value;
}
function ease(style, name, fallback) {
  const raw = style.getPropertyValue(name).trim();
  return raw || fallback;
}
function readMotionTokens(root) {
  if (!root || typeof getComputedStyle !== "function") return FALLBACK_MOTION;
  const style = getComputedStyle(root);
  return {
    durMicro: seconds(style, "--vitrina-dur-micro", FALLBACK_MOTION.durMicro),
    durUi: seconds(style, "--vitrina-dur-ui", FALLBACK_MOTION.durUi),
    durFlight: seconds(style, "--vitrina-dur-flight", FALLBACK_MOTION.durFlight),
    durPanel: seconds(style, "--vitrina-dur-panel", FALLBACK_MOTION.durPanel),
    easeMicro: ease(style, "--vitrina-ease-micro", FALLBACK_MOTION.easeMicro),
    easeFlight: ease(style, "--vitrina-ease-flight", FALLBACK_MOTION.easeFlight),
    staggerLine: seconds(style, "--vitrina-stagger-line", FALLBACK_MOTION.staggerLine),
    staggerLineExit: seconds(style, "--vitrina-stagger-line-exit", FALLBACK_MOTION.staggerLineExit)
  };
}

// src/reducedMotion.ts
import { useSyncExternalStore } from "react";
var REDUCE_QUERY = "(prefers-reduced-motion: reduce)";
function subscribe(onChange) {
  const query = window.matchMedia(REDUCE_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
var snapshot = () => window.matchMedia(REDUCE_QUERY).matches;
var serverSnapshot = () => false;
function usePrefersReducedMotion() {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}

// src/session.ts
function createSession() {
  const state = {
    pan: null,
    claimed: /* @__PURE__ */ new Set(),
    shown: /* @__PURE__ */ new Set(),
    introDone: false,
    flip: null
  };
  return {
    read: () => state,
    savePan(pan) {
      state.pan = pan;
    },
    claim(ids) {
      for (const id of ids) state.claimed.add(id);
    },
    unclaimPending() {
      for (const id of state.claimed) {
        if (!state.shown.has(id)) state.claimed.delete(id);
      }
    },
    markShown(id) {
      state.shown.add(id);
    },
    markIntroDone() {
      state.introDone = true;
    },
    resetIntro() {
      state.introDone = false;
    },
    storeFlip(record) {
      state.flip = record;
    },
    takeFlip() {
      const record = state.flip;
      state.flip = null;
      return record;
    }
  };
}

// src/plane/Plane.tsx
import { memo, useCallback, useMemo, useReducer, useRef, useState } from "react";
import { gsap as gsap2 } from "gsap";

// src/env.ts
function isProduction() {
  try {
    return process.env.NODE_ENV === "production";
  } catch {
    return false;
  }
}

// src/layout/rng.ts
function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = h << 13 | h >>> 19;
  }
  return () => {
    h = Math.imul(h ^ h >>> 16, 2246822507);
    h = Math.imul(h ^ h >>> 13, 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}
function mulberry32(a) {
  return () => {
    a = a + 1831565813 | 0;
    let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function createRng(seed) {
  return mulberry32(xmur3(seed)());
}

// src/layout/generate.ts
var COLUMN_STAGGER = 0.5;
var CELL_JITTER = 0.125;
var MAX_DEFERRALS = 8;
var clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
var floor2 = (v) => Math.floor(v * 100) / 100;
var chebyshev = (a, b) => Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
function repairSeparation(assignments, sep) {
  if (sep <= 0) return;
  const cleanAt = (entityId, cell, ignore) => {
    for (const other of assignments) {
      if (other.entityId !== entityId || ignore.includes(other)) continue;
      if (chebyshev(other.cell, cell) <= sep) return false;
    }
    return true;
  };
  for (let pass = 0; pass < 2; pass++) {
    let improved = false;
    for (const a of assignments) {
      if (cleanAt(a.entityId, a.cell, [a])) continue;
      for (const b of assignments) {
        if (b.entityId === a.entityId) continue;
        if (cleanAt(a.entityId, b.cell, [a, b]) && cleanAt(b.entityId, a.cell, [a, b])) {
          const tmp = a.entityId;
          a.entityId = b.entityId;
          b.entityId = tmp;
          improved = true;
          break;
        }
      }
    }
    if (!improved) break;
  }
}
function generatePlacedInstances(entities, layout) {
  const { world, count, columns, baseSize, sizeJitter, minSeparation, seed } = resolveLayout(layout);
  if (entities.length === 0 || count <= 0 || world.w <= 0 || world.h <= 0) return [];
  const byId = new Map(entities.map((e) => [e.id, e]));
  if (byId.size !== entities.length) {
    throw new TypeError("vitrina: entity ids must be unique");
  }
  const rng = createRng(seed);
  const cols = Math.max(1, Math.floor(columns));
  const step = world.w / cols;
  const rows = Math.max(1, Math.floor(world.h / step));
  const totalCells = cols * rows;
  const placeCount = Math.min(Math.floor(count), totalCells);
  const colOffsets = [];
  for (let c = 0; c < cols; c++) colOffsets.push(rng() * step * COLUMN_STAGGER);
  const cells = [];
  for (let i = 0; i < placeCount; i++) {
    const idx = Math.floor(i * totalCells / placeCount);
    cells.push({ col: idx % cols, row: Math.floor(idx / cols) });
  }
  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = cells[i];
    cells[i] = cells[j];
    cells[j] = tmp;
  }
  const fullSep = Math.max(0, Math.floor(minSeparation));
  const uses = /* @__PURE__ */ new Map();
  for (const e of entities) uses.set(e.id, 0);
  const placedCells = /* @__PURE__ */ new Map();
  const assignments = [];
  const separated = (entityId, cell, sep) => {
    const placed = placedCells.get(entityId);
    if (!placed) return true;
    for (const p of placed) {
      if (chebyshev(p, cell) <= sep) return false;
    }
    return true;
  };
  const queue = cells.map((cell) => ({
    cell,
    deferrals: 0
  }));
  for (let qi = 0; qi < queue.length; qi++) {
    const { cell, deferrals } = queue[qi];
    let minUse = Infinity;
    for (const n of uses.values()) if (n < minUse) minUse = n;
    const pool = entities.filter((e) => uses.get(e.id) === minUse);
    let candidates = pool.filter((e) => separated(e.id, cell, fullSep));
    if (candidates.length === 0) {
      if (deferrals < MAX_DEFERRALS) {
        queue.push({ cell, deferrals: deferrals + 1 });
        continue;
      }
      for (let sep = fullSep - 1; sep >= 0; sep--) {
        candidates = pool.filter((e) => separated(e.id, cell, sep));
        if (candidates.length > 0) break;
      }
    }
    const entity = candidates[Math.floor(rng() * candidates.length)];
    uses.set(entity.id, (uses.get(entity.id) ?? 0) + 1);
    let placed = placedCells.get(entity.id);
    if (!placed) {
      placed = [];
      placedCells.set(entity.id, placed);
    }
    placed.push(cell);
    assignments.push({ cell, entityId: entity.id });
  }
  assignments.sort((a, b) => a.cell.row - b.cell.row || a.cell.col - b.cell.col);
  for (let sep = fullSep; sep >= 1; sep--) repairSeparation(assignments, sep);
  const out = [];
  const counters = /* @__PURE__ */ new Map();
  for (const { cell, entityId } of assignments) {
    const jx = (rng() * 2 - 1) * step * CELL_JITTER;
    const jy = (rng() * 2 - 1) * step * CELL_JITTER;
    const entity = byId.get(entityId);
    const size = floor2(
      Math.min((entity.size ?? baseSize) * (1 + (rng() * 2 - 1) * sizeJitter), world.w, world.h)
    );
    const cx = (cell.col + 0.5) * step + jx;
    const cy = (cell.row + 0.5) * step + colOffsets[cell.col] + jy;
    const x = floor2(clamp(cx - size / 2, 0, world.w - size));
    const y = floor2(clamp(cy - size / 2, 0, world.h - size));
    const n = (counters.get(entityId) ?? 0) + 1;
    counters.set(entityId, n);
    out.push({ id: `${entityId}-${n}`, entityId, x, y, size, col: cell.col, row: cell.row });
  }
  return out;
}
function generateInstances(entities, layout) {
  return generatePlacedInstances(entities, layout).map((p) => ({
    id: p.id,
    entityId: p.entityId,
    x: p.x,
    y: p.y,
    size: p.size
  }));
}

// src/plane/geometry.ts
var screenToWorld = (s, view, pan, zoom) => (s - view / 2) / zoom + view / 2 - pan;
var centerPan = (world, viewport) => ({
  x: viewport.w / 2 - world.w / 2,
  y: viewport.h / 2 - world.h / 2
});
function panBounds(world, viewport, zoom) {
  const center = centerPan(world, viewport);
  if (zoom <= 0) {
    return { minX: center.x, maxX: center.x, minY: center.y, maxY: center.y };
  }
  const axis = (worldLen, viewLen, centered) => {
    const max = viewLen / 2 * (1 - 1 / zoom);
    const min = viewLen / 2 + viewLen / (2 * zoom) - worldLen;
    return min > max ? [centered, centered] : [min, max];
  };
  const [minX, maxX] = axis(world.w, viewport.w, center.x);
  const [minY, maxY] = axis(world.h, viewport.h, center.y);
  return { minX, maxX, minY, maxY };
}
var clampPan = (pan, b) => ({
  x: Math.min(b.maxX, Math.max(b.minX, pan.x)),
  y: Math.min(b.maxY, Math.max(b.minY, pan.y))
});
function visibleWorldRect(viewport, pan, zoom, inset = 0) {
  if (viewport.w <= 0 || viewport.h <= 0 || zoom <= 0) {
    return { left: 0, top: 0, right: 0, bottom: 0 };
  }
  return {
    left: screenToWorld(viewport.w * inset, viewport.w, pan.x, zoom),
    right: screenToWorld(viewport.w * (1 - inset), viewport.w, pan.x, zoom),
    top: screenToWorld(viewport.h * inset, viewport.h, pan.y, zoom),
    bottom: screenToWorld(viewport.h * (1 - inset), viewport.h, pan.y, zoom)
  };
}
var instanceVisible = (r, window2) => r.x < window2.right && r.x + r.size > window2.left && r.y < window2.bottom && r.y + r.size > window2.top;
var centreInside = (r, window2) => {
  const cx = r.x + r.size / 2;
  const cy = r.y + r.size / 2;
  return cx >= window2.left && cx <= window2.right && cy >= window2.top && cy <= window2.bottom;
};
function selectWorld(layout, viewportW, explicitInstances = false) {
  const compact = !explicitInstances && viewportW < layout.compactBreakpoint;
  return compact ? { world: layout.compactWorld, sizeFactor: layout.compactSizeFactor, compact } : { world: layout.world, sizeFactor: 1, compact };
}
function outOfWorld(instances, world) {
  const out = [];
  for (const i of instances) {
    if (i.x < 0 || i.y < 0 || i.x + i.size > world.w || i.y + i.size > world.h) out.push(i.id);
  }
  return out;
}

// src/plane/reveal.ts
function framePass(instances, viewport, pan, zoom, inset, revealed) {
  if (viewport.w <= 0 || viewport.h <= 0 || zoom <= 0 || instances.length === 0) {
    return { focusable: [], entering: [] };
  }
  const frame = visibleWorldRect(viewport, pan, zoom, 0);
  const insetFrame = inset > 0 ? visibleWorldRect(viewport, pan, zoom, inset) : frame;
  const focusable = [];
  const entering = [];
  for (const inst of instances) {
    if (revealed.has(inst.id)) {
      if (instanceVisible(inst, frame)) focusable.push(inst.id);
    } else if (centreInside(inst, insetFrame)) {
      entering.push(inst.id);
    }
  }
  return { focusable, entering };
}
function staggerDelays(count, rng, gap) {
  const [lo, hi] = gap;
  const delays = [];
  let t = 0;
  for (let i = 0; i < count; i++) {
    delays.push(t);
    t += lo + rng() * (hi - lo);
  }
  return delays;
}

// src/plane/Plane.tsx
import { jsx } from "react/jsx-runtime";
var contentOf = (el) => el.firstElementChild ?? el;
var hide = (targets, scale) => {
  gsap2.set(targets, { pointerEvents: "none", lazy: false });
  gsap2.set(targets.map(contentOf), { opacity: 0, scale, lazy: false });
};
var show = (targets) => {
  gsap2.set(targets, { pointerEvents: "auto", lazy: false });
  gsap2.set(targets.map(contentOf), { opacity: 1, scale: 1, lazy: false });
};
var PlaneObject = memo(function PlaneObject2({
  entity,
  instanceId,
  x,
  y,
  size,
  hidden,
  isActive,
  isRevealed,
  objectLabel,
  renderObject,
  nodeRef,
  onOpen
}) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      ref: nodeRef,
      type: "button",
      "data-vitrina-object": "",
      "data-vitrina-instance": instanceId,
      "data-vitrina-entity": entity.id,
      "data-flip-id": instanceId,
      "aria-label": objectLabel(entity),
      tabIndex: -1,
      onClick: () => onOpen(entity.id, instanceId),
      style: {
        left: x,
        top: y,
        width: size,
        height: size,
        // React owns `visibility`; GSAP owns opacity/scale/pointer-events.
        // Neither ever writes the other's. Everything else is base.css's.
        visibility: hidden ? "hidden" : void 0
      },
      children: /* @__PURE__ */ jsx("span", { "data-vitrina-object-content": "", children: renderObject(entity, { instanceId, isActive, isRevealed, view: "plane" }) })
    }
  );
});
function Plane({
  entities,
  instances,
  layout,
  renderObject,
  labels,
  zoom,
  reduced,
  session,
  hiddenIds,
  activeEntityId,
  onOpen,
  onNode,
  onDragStart,
  onGesture,
  motion
}) {
  const viewportRef = useRef(null);
  const zoomLayerRef = useRef(null);
  const panLayerRef = useRef(null);
  const zoomRef = useRef(zoom);
  const zoomAppliedRef = useRef(zoom);
  const draggableRef = useRef(null);
  const geometryRef = useRef(null);
  const placedOnceRef = useRef(false);
  const nodesRef = useRef(/* @__PURE__ */ new Map());
  const nodeRefsRef = useRef(/* @__PURE__ */ new Map());
  const inFrameRef = useRef(/* @__PURE__ */ new Set());
  const tabbableRef = useRef(/* @__PURE__ */ new Set());
  const revealCtxRef = useRef(null);
  const revealRngRef = useRef(null);
  const placedRef = useRef([]);
  const reducedRef = useRef(reduced);
  const onNodeRef = useRef(onNode);
  const onDragStartRef = useRef(onDragStart);
  const onOpenRef = useRef(onOpen);
  const onGestureRef = useRef(onGesture);
  const [, bumpRevealed] = useReducer((n) => n + 1, 0);
  const [geometry, setGeometry] = useState(null);
  const explicitInstances = instances !== void 0;
  useIsomorphicLayoutEffect(() => {
    return () => {
      const viewport = viewportRef.current;
      const plugins2 = getInteractionPlugins();
      if (!viewport || !plugins2 || !placedOnceRef.current || reducedRef.current) return;
      const { shown: shown2 } = session.read();
      const targets = [];
      for (const [id, el] of nodesRef.current) if (shown2.has(id)) targets.push(el);
      if (targets.length === 0) return;
      const state = plugins2.Flip.getState(targets);
      const rects = /* @__PURE__ */ new Map();
      for (const es of state.elementStates) {
        const id = es.element.getAttribute("data-vitrina-instance");
        if (id) rects.set(id, es.bounds);
      }
      session.storeFlip({
        from: "plane",
        state,
        rects,
        viewport: viewport.getBoundingClientRect()
      });
    };
  }, []);
  useIsomorphicLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const measure = () => {
      const { width, height } = viewport.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      const { world: world2, compact: compact2 } = selectWorld(layout, width, explicitInstances);
      const next = {
        viewW: width,
        viewH: height,
        worldW: world2.w,
        worldH: world2.h,
        compact: compact2
      };
      const current = geometryRef.current;
      if (current && current.viewW === next.viewW && current.viewH === next.viewH && current.worldW === next.worldW && current.worldH === next.worldH && // Compared too: a theme whose compactWorld equals its world still
      // changes the object SIZES across the breakpoint.
      current.compact === next.compact) {
        return;
      }
      geometryRef.current = next;
      setGeometry(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [layout, explicitInstances]);
  const compact = geometry?.compact ?? false;
  const placed = useMemo(() => {
    if (instances) return instances;
    if (!compact) return generateInstances(entities, layout);
    const factor = layout.compactSizeFactor;
    const scaledEntities = entities.map(
      (e) => e.size == null ? e : { ...e, size: e.size * factor }
    );
    return generateInstances(scaledEntities, {
      ...layout,
      world: layout.compactWorld,
      baseSize: layout.baseSize * factor
    });
  }, [instances, entities, layout, compact]);
  const world = geometry ? { w: geometry.worldW, h: geometry.worldH } : layout.world;
  const warnedWorldRef = useRef("");
  useIsomorphicLayoutEffect(() => {
    if (isProduction()) return;
    const stranded = outOfWorld(placed, world);
    if (stranded.length === 0) return;
    const key = `${world.w}x${world.h}:${stranded.join(",")}`;
    if (warnedWorldRef.current === key) return;
    warnedWorldRef.current = key;
    const sample = stranded.slice(0, 5).join(", ");
    console.warn(
      `[vitrina] ${stranded.length} of ${placed.length} instances fall outside the ${world.w}\xD7${world.h} world in use and can never be panned to: ${sample}${stranded.length > 5 ? `, +${stranded.length - 5} more` : ""}. Instance coordinates are absolute world px \u2014 grow \`layout.world\` to hold them, or place them inside it.`
    );
  }, [placed, world.w, world.h]);
  const entityById = useMemo(() => new Map(entities.map((e) => [e.id, e])), [entities]);
  useIsomorphicLayoutEffect(() => {
    placedRef.current = placed;
  }, [placed]);
  useIsomorphicLayoutEffect(() => {
    reducedRef.current = reduced;
  }, [reduced]);
  useIsomorphicLayoutEffect(() => {
    onNodeRef.current = onNode;
  }, [onNode]);
  useIsomorphicLayoutEffect(() => {
    onDragStartRef.current = onDragStart;
  }, [onDragStart]);
  useIsomorphicLayoutEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);
  useIsomorphicLayoutEffect(() => {
    onGestureRef.current = onGesture;
  }, [onGesture]);
  const handleOpen = useCallback(
    (entityId, instanceId) => onOpenRef.current(entityId, instanceId),
    []
  );
  const nodeRef = (id) => {
    let ref = nodeRefsRef.current.get(id);
    if (!ref) {
      ref = (el) => {
        onNodeRef.current(id, el);
        if (el) {
          nodesRef.current.set(id, el);
          const ctx = revealCtxRef.current;
          if (ctx && !session.read().shown.has(id)) addDetached(ctx, () => hide([el], REVEAL_SCALE));
        } else {
          nodesRef.current.delete(id);
        }
      };
      nodeRefsRef.current.set(id, ref);
    }
    return ref;
  };
  const markShown = (id, el) => {
    session.markShown(id);
    el.style.pointerEvents = "auto";
    el.setAttribute("data-vitrina-revealed", "");
    if (inFrameRef.current.has(id) && !tabbableRef.current.has(id)) {
      tabbableRef.current.add(id);
      el.tabIndex = 0;
    }
  };
  const reveal = (ids) => {
    const ctx = revealCtxRef.current;
    const rng = revealRngRef.current;
    if (!ctx || !rng) return;
    session.claim(ids);
    const intro = !session.read().introDone;
    session.markIntroDone();
    const batch = [];
    for (const id of ids) {
      const el = nodesRef.current.get(id);
      if (el) batch.push([id, el]);
    }
    if (batch.length === 0) return;
    addDetached(ctx, () => {
      if (reducedRef.current) {
        show(batch.map(([, el]) => el));
        for (const [id, el] of batch) markShown(id, el);
        bumpRevealed();
        return;
      }
      const delays = staggerDelays(batch.length, rng, REVEAL_GAP_MS);
      const from = intro ? INTRO_SCALE : REVEAL_SCALE;
      const duration = intro ? INTRO_POP_SECONDS : REVEAL_POP_SECONDS;
      batch.forEach(([id, el], i) => {
        gsap2.fromTo(
          contentOf(el),
          { opacity: 0, scale: from },
          {
            opacity: 1,
            scale: 1,
            duration,
            ease: REVEAL_POP_EASE,
            delay: (delays[i] ?? 0) / 1e3,
            // from/fromTo defer their initial state to the end of the tick by
            // default; the object must never paint a frame un-hidden before its pop.
            lazy: false,
            onStart: () => markShown(id, el),
            onComplete: i === batch.length - 1 ? bumpRevealed : void 0
          }
        );
      });
    });
  };
  const runPass = () => {
    const geo = geometryRef.current;
    const { pan, claimed, shown: shown2 } = session.read();
    if (!geo || !pan) return;
    const nodes = nodesRef.current;
    const { focusable, entering } = framePass(
      placedRef.current,
      { w: geo.viewW, h: geo.viewH },
      pan,
      zoomRef.current,
      REVEAL_INSET,
      claimed
    );
    const inFrame = new Set(focusable);
    for (const id of entering) inFrame.add(id);
    inFrameRef.current = inFrame;
    const next = /* @__PURE__ */ new Set();
    for (const id of focusable) if (shown2.has(id)) next.add(id);
    const prev = tabbableRef.current;
    for (const id of prev) {
      if (!next.has(id)) {
        const el = nodes.get(id);
        if (el) el.tabIndex = -1;
      }
    }
    for (const id of next) {
      if (!prev.has(id)) {
        const el = nodes.get(id);
        if (el) el.tabIndex = 0;
      }
    }
    tabbableRef.current = next;
    if (entering.length > 0) reveal(entering);
  };
  const syncPan = () => {
    const panLayer = panLayerRef.current;
    if (!panLayer) return;
    session.savePan({
      x: gsap2.getProperty(panLayer, "x"),
      y: gsap2.getProperty(panLayer, "y")
    });
    runPass();
  };
  useIsomorphicLayoutEffect(() => {
    const viewport = viewportRef.current;
    const zoomLayer = zoomLayerRef.current;
    const panLayer = panLayerRef.current;
    if (!viewport || !zoomLayer || !panLayer || !geometry) return;
    const view = { w: geometry.viewW, h: geometry.viewH };
    const world2 = { w: geometry.worldW, h: geometry.worldH };
    let cancelled = false;
    let drag = null;
    let wheel = null;
    let endGesture = null;
    const ctx = gsap2.context(() => {
      gsap2.set(zoomLayer, { scale: zoomRef.current });
      const start = clampPan(
        session.read().pan ?? centerPan(world2, view),
        panBounds(world2, view, zoom)
      );
      session.savePan(start);
      gsap2.set(panLayer, { x: start.x, y: start.y });
    }, viewport);
    placedOnceRef.current = true;
    runPass();
    void loadInteractionPlugins().then(({ Draggable, Observer }) => {
      if (cancelled) return;
      ctx.add(() => {
        const bounds = panBounds(world2, view, zoomAppliedRef.current);
        let moving = false;
        const promote = () => {
          if (moving) return;
          moving = true;
          onGestureRef.current(true);
          if (!reduced) gsap2.set(panLayer, { willChange: "transform" });
        };
        const demote = () => {
          if (!moving) return;
          if (drag && (drag.isDragging || drag.isThrowing)) return;
          if (panToX.tween?.isActive() || panToY.tween?.isActive()) return;
          moving = false;
          onGestureRef.current(false);
          if (!reduced) gsap2.set(panLayer, { willChange: "auto" });
        };
        endGesture = () => {
          if (!moving) return;
          moving = false;
          onGestureRef.current(false);
        };
        const { durMicro, easeMicro } = motion();
        const chase = reduced ? 0 : durMicro;
        const panToX = gsap2.quickTo(panLayer, "x", {
          duration: chase,
          ease: easeMicro,
          onUpdate: syncPan,
          onComplete: demote
        });
        const panToY = gsap2.quickTo(panLayer, "y", {
          duration: chase,
          ease: easeMicro,
          onUpdate: syncPan,
          onComplete: demote
        });
        Draggable.get(panLayer)?.kill();
        const [instance] = Draggable.create(panLayer, {
          trigger: viewport,
          type: "x,y",
          inertia: !reduced,
          zIndexBoost: false,
          edgeResistance: EDGE_RESISTANCE,
          // Below this the gesture is a click, not a drag: panning can start on
          // top of an object without activating it.
          minimumMovement: DRAG_THRESHOLD_PX,
          bounds,
          // The stylesheet's `cursor: grab` on the viewport would otherwise be
          // beaten by the inline `move` Draggable writes on its trigger.
          cursor: "grab",
          activeCursor: "grabbing",
          onPress: promote,
          // Fires once per gesture, only past minimumMovement — a click never
          // does. The root closes the panel here under dismissOn: 'planeDrag'.
          onDragStart: () => onDragStartRef.current?.(),
          onDrag: syncPan,
          onRelease: demote,
          onThrowUpdate: syncPan,
          onThrowComplete: demote
        });
        drag = instance ?? null;
        draggableRef.current = drag;
        wheel = Observer.create({
          target: viewport,
          type: "wheel",
          preventDefault: true,
          wheelSpeed: WHEEL_SPEED,
          onChange(self) {
            const { pan } = session.read();
            if (!pan) return;
            promote();
            const z = zoomRef.current;
            const target = clampPan(
              { x: pan.x - self.deltaX / z, y: pan.y - self.deltaY / z },
              panBounds(world2, view, z)
            );
            panToX(target.x);
            panToY(target.y);
          }
        });
      });
    });
    return () => {
      cancelled = true;
      endGesture?.();
      endGesture = null;
      drag?.kill();
      drag = null;
      draggableRef.current = null;
      wheel?.kill();
      wheel = null;
      revertContext(ctx);
    };
  }, [geometry, reduced]);
  useIsomorphicLayoutEffect(() => {
    const viewport = viewportRef.current;
    const zoomLayer = zoomLayerRef.current;
    const panLayer = panLayerRef.current;
    if (!viewport || !zoomLayer || !panLayer) return;
    let passAfter = false;
    const ctx = gsap2.context(() => {
      gsap2.set(zoomLayer, { scale: zoomRef.current });
      const { pan } = session.read();
      if (pan) gsap2.set(panLayer, { x: pan.x, y: pan.y });
      if (zoom === zoomAppliedRef.current) return;
      zoomAppliedRef.current = zoom;
      const geo = geometryRef.current;
      if (!geo) {
        zoomRef.current = zoom;
        gsap2.set(zoomLayer, { scale: zoom });
        return;
      }
      const view = { w: geo.viewW, h: geo.viewH };
      const world2 = { w: geo.worldW, h: geo.worldH };
      const bounds = panBounds(world2, view, zoom);
      const durUi = motion().durUi;
      const applyBounds = () => draggableRef.current?.applyBounds(bounds);
      const settled = pan ? clampPan(pan, bounds) : null;
      if (pan && settled && (settled.x !== pan.x || settled.y !== pan.y)) {
        if (reduced) {
          session.savePan(settled);
          gsap2.set(panLayer, { x: settled.x, y: settled.y });
          applyBounds();
        } else {
          gsap2.to(panLayer, {
            x: settled.x,
            y: settled.y,
            duration: durUi,
            ease: ZOOM_TWEEN_EASE,
            onUpdate: syncPan,
            onComplete: applyBounds
          });
        }
      } else {
        applyBounds();
      }
      if (reduced) {
        zoomRef.current = zoom;
        gsap2.set(zoomLayer, { scale: zoom });
        passAfter = true;
      } else {
        gsap2.set(zoomLayer, { willChange: "transform" });
        gsap2.to(zoomLayer, {
          scale: zoom,
          duration: durUi,
          ease: ZOOM_TWEEN_EASE,
          onUpdate: () => {
            zoomRef.current = gsap2.getProperty(zoomLayer, "scaleX");
            runPass();
          },
          onComplete: () => gsap2.set(zoomLayer, { willChange: "auto" })
        });
      }
    }, viewport);
    if (passAfter) runPass();
    return () => revertContext(ctx);
  }, [zoom, reduced]);
  const measured = geometry !== null;
  useGsapContext(
    (self) => {
      const panLayer = panLayerRef.current;
      if (!panLayer || !measured) return;
      revealCtxRef.current = self;
      revealRngRef.current ?? (revealRngRef.current = createRng(`${layout.seed}:reveal`));
      const { shown: shown2, introDone } = session.read();
      const hidden = [];
      const visible = [];
      for (const [id, el] of nodesRef.current) {
        if (shown2.has(id)) {
          visible.push(el);
          el.setAttribute("data-vitrina-revealed", "");
        } else {
          hidden.push(el);
        }
      }
      if (hidden.length > 0) hide(hidden, introDone ? REVEAL_SCALE : INTRO_SCALE);
      if (visible.length > 0) show(visible);
      for (const id of tabbableRef.current) {
        const el = nodesRef.current.get(id);
        if (el) el.tabIndex = -1;
      }
      tabbableRef.current = /* @__PURE__ */ new Set();
      runPass();
      return () => {
        revealCtxRef.current = null;
        session.unclaimPending();
        if (session.read().shown.size === 0) session.resetIntro();
      };
    },
    viewportRef,
    [measured]
  );
  useGsapContext(
    () => {
      if (!measured) return;
      const record = session.takeFlip();
      const plugins2 = getInteractionPlugins();
      if (!record || !plugins2 || reducedRef.current) return;
      const { shown: shown2 } = session.read();
      const targets = [];
      for (const [id, el] of nodesRef.current) if (shown2.has(id)) targets.push(el);
      if (targets.length === 0) return;
      plugins2.Flip.from(record.state, {
        targets,
        scale: true,
        prune: true,
        duration: VIEW_FLIP_SECONDS,
        ease: motion().easeFlight
      });
    },
    viewportRef,
    [measured]
  );
  const { shown } = session.read();
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: viewportRef,
      "data-vitrina-viewport": "",
      "data-lenis-prevent": "",
      role: "region",
      "aria-label": labels.viewport,
      children: /* @__PURE__ */ jsx("div", { ref: zoomLayerRef, "data-vitrina-zoom": "", children: /* @__PURE__ */ jsx("div", { ref: panLayerRef, "data-vitrina-pan": "", style: { width: world.w, height: world.h }, children: placed.map((inst) => {
        const entity = entityById.get(inst.entityId);
        if (!entity) return null;
        return /* @__PURE__ */ jsx(
          PlaneObject,
          {
            entity,
            instanceId: inst.id,
            x: inst.x,
            y: inst.y,
            size: inst.size,
            hidden: hiddenIds.has(inst.id),
            isActive: inst.entityId === activeEntityId,
            isRevealed: shown.has(inst.id),
            objectLabel: labels.objectLabel,
            renderObject,
            nodeRef: nodeRef(inst.id),
            onOpen: handleOpen
          },
          inst.id
        );
      }) }) })
    }
  );
}

// src/grid/Grid.tsx
import { useMemo as useMemo2, useRef as useRef2 } from "react";
import { gsap as gsap3 } from "gsap";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function Grid({
  entities,
  instances,
  layout,
  renderObject,
  renderCard,
  renderGridHeader,
  labels,
  reduced,
  session,
  hiddenIds,
  activeEntityId,
  onOpen,
  onNode,
  motion
}) {
  const rootRef = useRef2(null);
  const cardsRef = useRef2(/* @__PURE__ */ new Map());
  const cardRefsRef = useRef2(/* @__PURE__ */ new Map());
  const reducedRef = useRef2(reduced);
  const onNodeRef = useRef2(onNode);
  const instancesByEntity = useMemo2(() => {
    const placed = instances ?? generateInstances(entities, layout);
    const map = /* @__PURE__ */ new Map();
    for (const inst of placed) {
      const list = map.get(inst.entityId);
      if (list) list.push(inst.id);
      else map.set(inst.entityId, [inst.id]);
    }
    return map;
  }, [instances, entities, layout]);
  const representative = (entityId) => instancesByEntity.get(entityId)?.[0] ?? entityId;
  useIsomorphicLayoutEffect(() => {
    reducedRef.current = reduced;
  }, [reduced]);
  useIsomorphicLayoutEffect(() => {
    onNodeRef.current = onNode;
  }, [onNode]);
  const cardRef = (entityId) => {
    let ref = cardRefsRef.current.get(entityId);
    if (!ref) {
      ref = (el) => {
        onNodeRef.current(representative(entityId), el);
        if (el) cardsRef.current.set(entityId, el);
        else cardsRef.current.delete(entityId);
      };
      cardRefsRef.current.set(entityId, ref);
    }
    return ref;
  };
  useIsomorphicLayoutEffect(() => {
    return () => {
      const root = rootRef.current;
      const plugins2 = getInteractionPlugins();
      if (!root || !plugins2 || reducedRef.current) return;
      const targets = Array.from(cardsRef.current.values());
      if (targets.length === 0) return;
      const state = plugins2.Flip.getState(targets);
      const rects = /* @__PURE__ */ new Map();
      for (const es of state.elementStates) {
        const id = es.element.getAttribute("data-flip-id");
        if (id) rects.set(id, es.bounds);
      }
      session.storeFlip({ from: "grid", state, rects, viewport: root.getBoundingClientRect() });
    };
  }, []);
  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const record = session.takeFlip();
    const plugins2 = getInteractionPlugins();
    if (!record || !plugins2 || reducedRef.current) return;
    const cards = cardsRef.current;
    if (cards.size === 0) return;
    if (record.from === "plane") pairCards(record, cards, instancesByEntity, representative);
    const ctx = gsap3.context(() => {
      gsap3.set(root, { overflowY: "hidden" });
      plugins2.Flip.from(record.state, {
        targets: Array.from(cards.values()),
        scale: true,
        prune: true,
        duration: VIEW_FLIP_SECONDS,
        ease: motion().easeFlight,
        /*
         * Cards with no shown object to come from. The card's COPY fades with
         * its object — a caption already sitting there under an object still
         * fading in reads as two unrelated arrivals. The object node stays in
         * the target list (it is the one Flip handed us); the caption joins it.
         * Not the item wrapper: keeping both nodes explicit keeps the fade off
         * a box whose only job is layout, and keeps the OBJECT in the target
         * list, which is what the view test can still see. (The pairing of the
         * copy is only observable in a real browser: under StrictMode this
         * context is built, reverted and rebuilt, and by the second run there
         * are no entering elements left to call this.)
         */
        onEnter: (elements) => gsap3.fromTo(
          elements.flatMap((el) => {
            const copy = el.parentElement?.querySelector("[data-vitrina-card-content]");
            return copy ? [el, copy] : [el];
          }),
          { opacity: 0 },
          { opacity: 1, duration: VIEW_FLIP_SECONDS, ease: "power2.out" }
        ),
        onComplete: () => gsap3.set(root, { overflowY: "auto" })
      });
    }, root);
    return () => revertContext(ctx);
  }, []);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: rootRef,
      "data-vitrina-grid": "",
      "data-lenis-prevent": "",
      role: "region",
      "aria-label": labels.grid ?? labels.viewport,
      children: [
        renderGridHeader && /* @__PURE__ */ jsx2("div", { "data-vitrina-grid-header": "", children: renderGridHeader() }),
        entities.map((entity) => {
          const instanceId = representative(entity.id);
          const ctx = {
            instanceId,
            isActive: entity.id === activeEntityId,
            isRevealed: true,
            view: "grid"
          };
          return (
            // The grid cell. The button inside it keeps the object's exact box (it
            // is what flies); the card content sits beneath it, out of the flight.
            /* @__PURE__ */ jsxs("div", { "data-vitrina-grid-item": "", children: [
              /* @__PURE__ */ jsx2(
                "button",
                {
                  ref: cardRef(entity.id),
                  type: "button",
                  "data-vitrina-object": "",
                  "data-vitrina-card": "",
                  "data-vitrina-instance": instanceId,
                  "data-vitrina-entity": entity.id,
                  "data-flip-id": instanceId,
                  "aria-label": labels.objectLabel(entity),
                  onClick: () => onOpen(entity.id, instanceId),
                  style: { visibility: hiddenIds.has(instanceId) ? "hidden" : void 0 },
                  children: /* @__PURE__ */ jsx2("span", { "data-vitrina-object-content": "", children: renderObject(entity, ctx) })
                }
              ),
              renderCard && /* @__PURE__ */ jsx2("div", { "data-vitrina-card-content": "", children: renderCard(entity, ctx) })
            ] }, entity.id)
          );
        })
      ]
    }
  );
}
function pairCards(record, cards, instancesByEntity, representative) {
  const cx = record.viewport.left + record.viewport.width / 2;
  const cy = record.viewport.top + record.viewport.height / 2;
  for (const [entityId, card] of cards) {
    let best = null;
    let bestDistance = Infinity;
    for (const id of instancesByEntity.get(entityId) ?? []) {
      const r = record.rects.get(id);
      if (!r) continue;
      const dx = r.left + r.width / 2 - cx;
      const dy = r.top + r.height / 2 - cy;
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = id;
      }
    }
    card.setAttribute("data-flip-id", best ?? representative(entityId));
  }
}

// src/detail/Detail.tsx
import { createPortal } from "react-dom";
import { Fragment, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function Detail({
  entity,
  instanceId,
  panel,
  copy,
  panelAnim,
  view,
  side,
  besidePlacement,
  modal,
  relayEntity,
  relayInstanceId,
  relayFlying,
  portalReady,
  portalTextStyle,
  renderObject,
  renderAbove,
  renderBeside,
  renderDetail,
  renderBelow,
  renderClose,
  labels,
  detailContext,
  panelRef,
  cardRef,
  slotRef,
  flightRef,
  relayRef
}) {
  const objectContext = {
    instanceId: instanceId ?? entity.id,
    isActive: true,
    isRevealed: true,
    view
  };
  const flightLayers = (
    /*
     * Stamped `data-vitrina-root` so the stacking tokens and any theme custom
     * properties (all scoped to `[data-vitrina-root]`) resolve here too — this
     * subtree lives on `body`, outside the widget's own root. `pointer-events:
     * none`, `aria-hidden`: it is decoration, and the panel/plane keep the
     * hit-test. Both the active clone and the relay are FLYING objects on the
     * flight rung; two layers only so they can move independently (crossfade).
     */
    /* @__PURE__ */ jsxs2("div", { "data-vitrina-root": "", "data-vitrina-flight-portal": "", "aria-hidden": "true", style: portalTextStyle, children: [
      /* @__PURE__ */ jsx3("div", { "data-vitrina-relay-layer": "", children: /* @__PURE__ */ jsx3(
        "div",
        {
          ref: relayRef,
          "data-vitrina-relay": "",
          style: { visibility: relayFlying ? "visible" : "hidden" },
          children: relayEntity && renderObject(relayEntity, {
            instanceId: relayInstanceId ?? relayEntity.id,
            isActive: true,
            isRevealed: true,
            view
          })
        }
      ) }),
      /* @__PURE__ */ jsx3("div", { "data-vitrina-flight-layer": "", children: /* @__PURE__ */ jsx3(
        "div",
        {
          ref: flightRef,
          "data-vitrina-flight": "",
          style: { visibility: copy === "flight" ? "visible" : "hidden" },
          children: renderObject(entity, objectContext)
        }
      ) })
    ] })
  );
  const slot = /* @__PURE__ */ jsx3(
    "div",
    {
      ref: slotRef,
      "data-vitrina-slot": "",
      style: { visibility: copy === "panel" ? "visible" : "hidden" },
      children: renderObject(entity, objectContext)
    }
  );
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx3("div", { "data-vitrina-detail": "", "data-vitrina-panel-phase": panel, children: /* @__PURE__ */ jsxs2(
      "div",
      {
        ref: panelRef,
        "data-vitrina-panel": "",
        "data-vitrina-panel-side": side,
        "data-vitrina-panel-phase": panel,
        "data-vitrina-panel-anim": panelAnim,
        "data-lenis-prevent": "",
        role: "dialog",
        "aria-modal": modal ? "true" : void 0,
        "aria-label": labels.objectLabel(entity),
        tabIndex: -1,
        children: [
          /* @__PURE__ */ jsx3("div", { ref: cardRef, "data-vitrina-panel-card": "", children: /* @__PURE__ */ jsxs2("div", { "data-vitrina-panel-content": "", children: [
            renderAbove?.(entity, detailContext),
            /* @__PURE__ */ jsxs2("div", { "data-vitrina-panel-row": "", children: [
              besidePlacement === "start" && renderBeside?.(entity, detailContext),
              slot,
              besidePlacement === "end" && renderBeside?.(entity, detailContext)
            ] }),
            /* @__PURE__ */ jsx3("div", { "data-vitrina-detail-content": "", children: renderDetail?.(entity, detailContext) }),
            renderBelow?.(entity, detailContext)
          ] }) }),
          renderClose ? /* @__PURE__ */ jsx3("div", { "data-vitrina-panel-fixed": "", children: renderClose(detailContext) }) : null
        ]
      }
    ) }),
    portalReady ? createPortal(flightLayers, document.body) : null
  ] });
}

// src/detail/machine.ts
var CLOSED = Object.freeze({
  panel: "closed",
  active: null,
  flight: "shown",
  relaying: null,
  queued: null
});
var S = (panel, active, flight, relaying = null, queued = null) => ({ panel, active, flight, relaying, queued });
function initialDetail(entityId) {
  return entityId ? S("open", { entityId, instanceId: null }, "shown") : CLOSED;
}
var reqOf = (a) => ({
  entityId: a.entityId,
  instanceId: a.instanceId,
  animate: a.animate,
  collision: a.collision
});
var relayFlies = (r) => r.animate && r.instanceId !== null && r.collision !== "none";
function relayInto(state, r) {
  const active = { entityId: r.entityId, instanceId: r.instanceId };
  const flies = relayFlies(r);
  const outgoing = r.collision !== "none" && state.active && state.active.instanceId !== null ? { entityId: state.active.entityId, instanceId: state.active.instanceId } : null;
  if (!flies) return S("open", active, "shown", outgoing);
  if (r.collision === "serialize" && outgoing) return S("open", active, "waiting", outgoing);
  return S("open", active, "in", outgoing);
}
function beginInOpen(r) {
  return S("open", { entityId: r.entityId, instanceId: r.instanceId }, relayFlies(r) ? "in" : "shown");
}
function drain(state) {
  if (state.relaying === null && state.flight === "shown" && state.queued !== null) {
    return relayInto(state, state.queued);
  }
  return state;
}
function transition(state, action) {
  switch (action.type) {
    case "open": {
      const r = reqOf(action);
      const flies = r.animate && r.instanceId !== null;
      const active = { entityId: r.entityId, instanceId: r.instanceId };
      if (state.panel !== "open") return S("open", active, flies ? "waiting" : "shown");
      if (state.active && state.active.entityId === r.entityId && state.flight !== "out") {
        return state;
      }
      if (state.active && state.active.entityId === r.entityId && state.flight === "out") {
        return S("open", active, flies ? "in" : "shown");
      }
      if (!flies) return S("open", active, "shown");
      const busy = state.relaying !== null || state.flight === "waiting" || state.flight === "in";
      if (busy) return { ...state, queued: r };
      return relayInto(state, r);
    }
    case "close": {
      if (state.panel !== "open") return state;
      const canFlyHome = action.animate && state.active?.instanceId != null && state.flight === "shown";
      if (canFlyHome) return S("covering", state.active, "leaving");
      if (action.animate) return S("covering", state.active, "shown");
      return CLOSED;
    }
    case "revealed": {
      if (state.flight === "waiting" && state.relaying === null) return S("open", state.active, "in");
      return state;
    }
    case "landed": {
      if (state.flight === "in") return drain(S("open", state.active, "shown", state.relaying, state.queued));
      if (state.flight === "out") return CLOSED;
      return state;
    }
    case "relayLanded": {
      if (state.relaying === null) return state;
      if (state.flight === "waiting") {
        if (state.queued) return beginInOpen(state.queued);
        return S("open", state.active, "in");
      }
      return drain(S("open", state.active, state.flight, null, state.queued));
    }
    case "coverDone": {
      if (state.panel !== "covering") return state;
      if (state.flight === "leaving") return S("covering", state.active, "out");
      return CLOSED;
    }
    case "detach": {
      if (state.panel === "closed") return state;
      if (state.panel === "covering") return CLOSED;
      if (state.active && state.active.instanceId === null && state.relaying === null && state.queued === null && state.flight === "shown") {
        return state;
      }
      return S("open", state.active ? { entityId: state.active.entityId, instanceId: null } : null, "shown");
    }
    case "abandon":
      return state.panel === "closed" ? state : CLOSED;
  }
}
function activeCopy(state) {
  if (state.active === null) return "plane";
  const hasOrigin = state.active.instanceId !== null;
  switch (state.flight) {
    case "waiting":
      return state.relaying === null && hasOrigin ? "flight" : "plane";
    case "in":
    case "leaving":
    case "out":
      return hasOrigin ? "flight" : "panel";
    case "shown":
      return "panel";
  }
}
var NONE_HIDDEN = /* @__PURE__ */ new Set();
function hiddenInstancesOf(state) {
  const activeId = state.active?.instanceId != null && activeCopy(state) !== "plane" ? state.active.instanceId : null;
  if (activeId === null && state.relaying === null) return NONE_HIDDEN;
  const set = /* @__PURE__ */ new Set();
  if (activeId !== null) set.add(activeId);
  if (state.relaying !== null) set.add(state.relaying.instanceId);
  return set;
}
var panelPresent = (state) => state.panel !== "closed";

// src/Vitrina.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var clampIndex = (index, count) => count <= 0 ? 0 : Math.min(count - 1, Math.max(0, Math.floor(index)));
var DEFAULT_DISMISS = ["escape"];
function Vitrina({
  entities,
  instances,
  layout,
  renderObject,
  renderCard,
  renderGridHeader,
  renderAbove,
  renderBeside,
  renderDetail,
  renderBelow,
  renderClose,
  panelSide = "right",
  besidePlacement = "start",
  dismissOn = DEFAULT_DISMISS,
  modal = false,
  activeId: activeIdProp,
  defaultActiveId = null,
  onActiveChange,
  openCollision = "serialize",
  view: viewProp,
  defaultView = "plane",
  onViewChange,
  zoomSteps = DEFAULT_ZOOM_STEPS,
  defaultZoomIndex = DEFAULT_ZOOM_INDEX,
  reducedMotion = "respect",
  labels,
  className,
  style,
  children
}) {
  const resolvedLayout = useMemo3(() => resolveLayout(layout), [layout]);
  const stepCount = zoomSteps.length;
  const [zoomIndex, setZoomIndex] = useState2(() => clampIndex(defaultZoomIndex, stepCount));
  const [session] = useState2(createSession);
  const prefersReduced = usePrefersReducedMotion();
  const reduced = reducedMotion === "ignore" ? false : prefersReduced;
  const viewLocked = reducedMotion === "grid" && prefersReduced;
  const reducedRef = useRef3(reduced);
  useIsomorphicLayoutEffect(() => {
    reducedRef.current = reduced;
  }, [reduced]);
  const [internalView, setInternalView] = useState2(defaultView);
  const controlledView = viewProp !== void 0;
  const view = viewLocked ? "grid" : controlledView ? viewProp : internalView;
  const setView = useCallback2(
    (next) => {
      if (viewLocked || next === view) return;
      onViewChange?.(next);
      if (!controlledView) setInternalView(next);
    },
    [viewLocked, view, controlledView, onViewChange]
  );
  const index = clampIndex(zoomIndex, stepCount);
  const zoom = zoomSteps[index] ?? 1;
  const entityById = useMemo3(() => new Map(entities.map((e) => [e.id, e])), [entities]);
  const rootRef = useRef3(null);
  const panelRef = useRef3(null);
  const cardRef = useRef3(null);
  const slotRef = useRef3(null);
  const flightRef = useRef3(null);
  const relayRef = useRef3(null);
  const collisionRef = useRef3(openCollision);
  useIsomorphicLayoutEffect(() => {
    collisionRef.current = openCollision;
  }, [openCollision]);
  const [portalReady, setPortalReady] = useState2(false);
  const portalTextStyleRef = useRef3(void 0);
  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;
    if (root) {
      const cs = getComputedStyle(root);
      portalTextStyleRef.current = {
        fontFamily: cs.fontFamily,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        fontStyle: cs.fontStyle,
        letterSpacing: cs.letterSpacing,
        wordSpacing: cs.wordSpacing,
        textTransform: cs.textTransform,
        direction: cs.direction
      };
    }
    setPortalReady(true);
  }, []);
  const motionRef = useRef3(null);
  useIsomorphicLayoutEffect(() => {
    motionRef.current = readMotionTokens(rootRef.current);
  }, []);
  const motion = useCallback2(() => motionRef.current ?? FALLBACK_MOTION, []);
  const nodesRef = useRef3(/* @__PURE__ */ new Map());
  const registerNode = useCallback2((id, el) => {
    if (el) nodesRef.current.set(id, el);
    else nodesRef.current.delete(id);
  }, []);
  const controlledActive = activeIdProp !== void 0;
  const [detail, dispatch] = useReducer2(
    transition,
    controlledActive ? activeIdProp : defaultActiveId,
    initialDetail
  );
  const detailRef = useRef3(detail);
  useIsomorphicLayoutEffect(() => {
    detailRef.current = detail;
  }, [detail]);
  const pendingOriginRef = useRef3(null);
  const [syncedActive, setSyncedActive] = useState2(activeIdProp);
  if (controlledActive && activeIdProp !== syncedActive) {
    setSyncedActive(activeIdProp);
    if (activeIdProp === null) {
      dispatch({ type: "close", animate: !reduced });
    } else {
      const pending = pendingOriginRef.current;
      const matches = pending !== null && pending.entityId === activeIdProp;
      dispatch({
        type: "open",
        entityId: activeIdProp,
        instanceId: matches ? pending.instanceId : null,
        animate: matches ? pending.animate : false,
        collision: openCollision
      });
    }
  }
  useIsomorphicLayoutEffect(() => {
    pendingOriginRef.current = null;
  }, [detail]);
  const [syncedView, setSyncedView] = useState2(view);
  if (view !== syncedView) {
    setSyncedView(view);
    if (panelPresent(detail)) dispatch({ type: "detach" });
  }
  const activeEntityId = detail.active?.entityId ?? null;
  const activeEntity = activeEntityId !== null ? entityById.get(activeEntityId) ?? null : null;
  if (activeEntityId !== null && activeEntity === null) dispatch({ type: "abandon" });
  const pickOrigin = useCallback2((entityId) => {
    const root = rootRef.current;
    if (!root) return null;
    const r = root.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    let best = null;
    let bestDistance = Infinity;
    for (const [id, el] of nodesRef.current) {
      if (el.getAttribute("data-vitrina-entity") !== entityId) continue;
      if (!el.hasAttribute("data-vitrina-revealed") && !el.hasAttribute("data-vitrina-card")) continue;
      const b = el.getBoundingClientRect();
      const dx = b.left + b.width / 2 - cx;
      const dy = b.top + b.height / 2 - cy;
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = id;
      }
    }
    return best;
  }, []);
  const activate = useCallback2(
    (entityId, instanceId, animate) => {
      const shouldAnimate = animate && !reducedRef.current;
      const collision = collisionRef.current;
      if (controlledActive) {
        pendingOriginRef.current = { entityId, instanceId, animate: shouldAnimate };
        if (activeIdProp === entityId) {
          dispatch({ type: "open", entityId, instanceId, animate: shouldAnimate, collision });
        } else {
          onActiveChange?.(entityId);
        }
        return;
      }
      const current = detailRef.current;
      const changes = current.active?.entityId !== entityId || current.panel !== "open";
      if (changes) onActiveChange?.(entityId);
      dispatch({ type: "open", entityId, instanceId, animate: shouldAnimate, collision });
    },
    [controlledActive, activeIdProp, onActiveChange]
  );
  const openDetail = useCallback2(
    (entityId, instanceId = null) => {
      if (!entityById.has(entityId)) return;
      activate(entityId, instanceId, true);
    },
    [entityById, activate]
  );
  const closeDetail = useCallback2(() => {
    const current = detailRef.current;
    if (current.panel !== "open") return;
    onActiveChange?.(null);
    if (!controlledActive) dispatch({ type: "close", animate: !reducedRef.current });
  }, [controlledActive, onActiveChange]);
  const stepDetail = useCallback2(
    (delta) => {
      const current = detailRef.current;
      const currentId = current.active?.entityId;
      if (currentId == null || current.panel !== "open") return;
      const at = entities.findIndex((e) => e.id === currentId);
      if (at < 0) return;
      const next = entities[(at + delta + entities.length) % entities.length];
      if (!next) return;
      activate(next.id, pickOrigin(next.id), true);
    },
    [entities, activate, pickOrigin]
  );
  const objectSettled = detail.panel === "open" && detail.flight === "shown";
  const ctxActiveId = detail.active?.entityId ?? null;
  const detailContext = useMemo3(
    () => ({
      close: closeDetail,
      step: stepDetail,
      activeId: ctxActiveId,
      view,
      objectSettled
    }),
    [closeDetail, stepDetail, ctxActiveId, view, objectSettled]
  );
  const flyVisual = (visual, from, to, seconds2, onComplete) => {
    gsap4.set(visual, {
      left: to.left,
      top: to.top,
      width: to.width,
      height: to.height,
      transformOrigin: "0 0",
      x: from.left - to.left,
      y: from.top - to.top,
      scaleX: to.width === 0 ? 1 : from.width / to.width,
      scaleY: to.height === 0 ? 1 : from.height / to.height,
      // GSAP rounds a positioned element's left/top/width/height to whole px;
      // the destination is a fractional rect, and a box snapped to integers
      // lands up to half a pixel off the copy it hands over to.
      autoRound: false,
      // Promoted for the flight only; the context revert in the landing commit
      // strips it — never permanent (§7).
      willChange: "transform"
    });
    gsap4.to(visual, {
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      autoRound: false,
      duration: seconds2,
      ease: motion().easeFlight,
      onComplete
    });
  };
  const resumeFromRef = useRef3(null);
  const activeFlightKey = `${portalReady ? "p" : "-"}:${detail.flight}:${detail.relaying === null ? "own" : "relay"}:${detail.active?.instanceId ?? ""}:${panelSide}`;
  useIsomorphicLayoutEffect(() => {
    const resume = resumeFromRef.current;
    resumeFromRef.current = null;
    const { flight, active, relaying } = detail;
    const hasOrigin = active?.instanceId != null;
    const parkingAtOrigin = flight === "waiting" && relaying === null && hasOrigin;
    const parkingAtSlot = flight === "leaving" && hasOrigin;
    const parking = parkingAtOrigin || parkingAtSlot;
    const flying = flight === "in" || flight === "out";
    if (!parking && !flying) return;
    const settle = () => dispatch({ type: "landed" });
    const instanceId = active?.instanceId ?? null;
    const origin = instanceId !== null ? nodesRef.current.get(instanceId) : void 0;
    const slot = slotRef.current;
    const visual = flightRef.current;
    const root = rootRef.current;
    if (!origin || !slot || !visual || !root) {
      if (flying) settle();
      return;
    }
    if (parking) {
      const box = (parkingAtSlot ? slot : origin).getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return;
      const ctx2 = gsap4.context(() => {
        gsap4.set(visual, {
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          transformOrigin: "0 0",
          x: 0,
          y: 0,
          scaleX: 1,
          scaleY: 1,
          // Same as the flight: a box rounded to whole px sits visibly off the
          // fractional rect of the copy it replaces.
          autoRound: false
        });
      }, root);
      return () => revertContext(ctx2);
    }
    const from = resume ?? (flight === "in" ? origin : slot).getBoundingClientRect();
    const to = (flight === "in" ? slot : origin).getBoundingClientRect();
    if (from.width === 0 || from.height === 0 || to.width === 0 || to.height === 0) {
      settle();
      return;
    }
    const ctx = gsap4.context(() => {
      flyVisual(visual, from, to, motion().durFlight, () => {
        if (flight === "in") slot.style.visibility = "visible";
        else origin.style.visibility = "";
        settle();
      });
    }, root);
    return () => {
      resumeFromRef.current = visual.getBoundingClientRect();
      revertContext(ctx);
    };
  }, [activeFlightKey]);
  const relayInstanceId = detail.relaying?.instanceId ?? null;
  useIsomorphicLayoutEffect(() => {
    if (relayInstanceId === null) return;
    const done = () => dispatch({ type: "relayLanded" });
    const origin = nodesRef.current.get(relayInstanceId);
    const slot = slotRef.current;
    const visual = relayRef.current;
    const root = rootRef.current;
    if (!origin || !slot || !visual || !root) {
      done();
      return;
    }
    const from = slot.getBoundingClientRect();
    const to = origin.getBoundingClientRect();
    if (from.width === 0 || from.height === 0 || to.width === 0 || to.height === 0) {
      done();
      return;
    }
    const ctx = gsap4.context(() => {
      flyVisual(visual, from, to, motion().durFlight, () => {
        origin.style.visibility = "";
        done();
      });
    }, root);
    return () => revertContext(ctx);
  }, [relayInstanceId]);
  const panelPhase = detail.panel;
  const prevPanelRef = useRef3(detail.panel);
  useIsomorphicLayoutEffect(() => {
    const prev = prevPanelRef.current;
    prevPanelRef.current = panelPhase;
    const root = rootRef.current;
    const reduced2 = reducedRef.current;
    const seconds2 = reduced2 ? 0 : motion().durPanel;
    const lines = reduced2 ? [] : Array.from(panelRef.current?.querySelectorAll("[data-vitrina-line]") ?? []);
    if (panelPhase === "open" && prev !== "open") {
      const release = () => dispatch({ type: "revealed" });
      let ctx;
      if (lines.length > 0) {
        const step = motion().staggerLine;
        const delay = panelAnimRef.current === "reveal" ? seconds2 : 0;
        ctx = gsap4.context(() => {
          gsap4.fromTo(
            lines,
            { opacity: 0, y: DETAIL_LINE_SHIFT },
            {
              opacity: 1,
              y: 0,
              duration: seconds2,
              ease: DETAIL_LINE_EASE,
              delay,
              stagger: step,
              lazy: false
            }
          );
        }, root ?? void 0);
      }
      if (seconds2 === 0) {
        release();
        return () => revertContext(ctx);
      }
      const t = gsap4.delayedCall(seconds2, release);
      return () => {
        t.kill();
        revertContext(ctx);
      };
    }
    if (panelPhase === "covering") {
      const done = () => dispatch({ type: "coverDone" });
      let ctx;
      let total = seconds2;
      if (lines.length > 0) {
        const step = motion().staggerLineExit;
        total = Math.max(total, (lines.length - 1) * step + seconds2);
        ctx = gsap4.context(() => {
          gsap4.fromTo(
            lines,
            { opacity: 1, y: 0 },
            {
              opacity: 0,
              y: DETAIL_LINE_SHIFT,
              duration: seconds2,
              ease: DETAIL_LINE_EXIT_EASE,
              stagger: { each: step, from: "end" },
              // The lines are already at their start state; forcing it would
              // stomp anything the consumer set inline.
              immediateRender: false,
              lazy: false
            }
          );
        }, root ?? void 0);
      }
      if (total === 0) {
        done();
        return () => revertContext(ctx);
      }
      const t = gsap4.delayedCall(total, done);
      return () => {
        t.kill();
        revertContext(ctx);
      };
    }
  }, [panelPhase]);
  const contentEntityId = detail.panel === "open" ? detail.active?.entityId ?? null : null;
  const prevContentEntityRef = useRef3(contentEntityId);
  useIsomorphicLayoutEffect(() => {
    const prev = prevContentEntityRef.current;
    prevContentEntityRef.current = contentEntityId;
    if (contentEntityId === null || prev === null || prev === contentEntityId) return;
    if (reducedRef.current) return;
    const root = rootRef.current;
    const content = panelRef.current?.querySelector("[data-vitrina-panel-content]");
    const lines = content ? Array.from(content.querySelectorAll("[data-vitrina-line]")) : [];
    if (lines.length === 0) return;
    const step = motion().staggerLine;
    const seconds2 = motion().durPanel;
    const ctx = gsap4.context(() => {
      gsap4.fromTo(
        lines,
        { opacity: 0, y: DETAIL_LINE_SHIFT },
        {
          opacity: 1,
          y: 0,
          duration: seconds2,
          ease: DETAIL_LINE_EASE,
          stagger: step,
          lazy: false,
          overwrite: "auto"
        }
      );
    }, root ?? void 0);
    return () => revertContext(ctx);
  }, [contentEntityId]);
  const cardHeightRef = useRef3(null);
  useIsomorphicLayoutEffect(() => {
    const card = cardRef.current;
    const root = rootRef.current;
    if (!card || !root || detail.panel !== "open") {
      cardHeightRef.current = card && detail.panel === "open" ? card.offsetHeight : null;
      return;
    }
    const next = card.scrollHeight;
    const prev = cardHeightRef.current;
    cardHeightRef.current = next;
    if (prev === null || prev === next || reducedRef.current) return;
    const seconds2 = motion().durPanel;
    const ctx = gsap4.context(() => {
      gsap4.fromTo(
        card,
        { height: prev },
        {
          height: next,
          duration: seconds2,
          ease: DETAIL_PANEL_EASE,
          // Back to auto: a fixed height would clip content that later grows (a scroll).
          onComplete: () => gsap4.set(card, { height: "auto" }),
          clearProps: "height"
        }
      );
    }, root);
    return () => revertContext(ctx);
  }, [detail.active?.entityId, detail.panel]);
  const prevPresentRef = useRef3(panelPresent(detail));
  const returnToRef = useRef3(detail.active?.instanceId ?? null);
  useIsomorphicLayoutEffect(() => {
    const present = panelPresent(detail);
    const was = prevPresentRef.current;
    prevPresentRef.current = present;
    if (present) {
      if (detail.active?.instanceId != null) returnToRef.current = detail.active.instanceId;
      if (!was) panelRef.current?.focus({ preventScroll: true });
      return;
    }
    if (!was) return;
    const id = returnToRef.current;
    returnToRef.current = null;
    const target = (id !== null ? nodesRef.current.get(id) : void 0) ?? rootRef.current;
    target?.focus({ preventScroll: true });
  }, [detail]);
  const panelOpen = detail.panel === "open";
  const dismissEscape = dismissOn.includes("escape");
  const dismissOutside = dismissOn.includes("outside");
  const dismissPlaneDrag = dismissOn.includes("planeDrag");
  useEffect2(() => {
    if (!panelOpen || !dismissEscape) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !event.defaultPrevented) closeDetail();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [panelOpen, dismissEscape, closeDetail]);
  useEffect2(() => {
    if (!panelOpen || !dismissOutside) return;
    const onClick = (event) => {
      if (event.defaultPrevented) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (panelRef.current?.contains(target)) return;
      if (target.closest("[data-vitrina-object]")) return;
      closeDetail();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [panelOpen, dismissOutside, closeDetail]);
  const dismissPlaneDragRef = useRef3(dismissPlaneDrag);
  useIsomorphicLayoutEffect(() => {
    dismissPlaneDragRef.current = dismissPlaneDrag;
  }, [dismissPlaneDrag]);
  const onPlaneDragStart = useCallback2(() => {
    if (dismissPlaneDragRef.current) closeDetail();
  }, [closeDetail]);
  const onPlaneGesture = useCallback2((moving) => {
    const root = rootRef.current;
    if (!root) return;
    if (moving) root.setAttribute("data-vitrina-gesture", "");
    else root.removeAttribute("data-vitrina-gesture");
  }, []);
  useEffect2(() => {
    if (!panelOpen || !modal) return;
    const onKeyDown = (event) => {
      if (event.key !== "Tab" || event.defaultPrevented) return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll(
          "a[href], button, input, select, textarea, [tabindex]"
        )
      ).filter(
        (el) => !el.disabled && el.getAttribute("tabindex") !== "-1"
      );
      const active = document.activeElement;
      if (focusables.length === 0) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const index2 = active instanceof HTMLElement ? focusables.indexOf(active) : -1;
      if (index2 === -1) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [panelOpen, modal]);
  const warnedCoverageRef = useRef3(false);
  useEffect2(() => {
    if (!panelOpen || modal || warnedCoverageRef.current) return;
    if (isProduction()) return;
    const rootRect = rootRef.current?.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    if (!rootRect || !panelRect || rootRect.width === 0 || rootRect.height === 0) return;
    const coverage = panelRect.width * panelRect.height / (rootRect.width * rootRect.height);
    if (coverage >= 0.95) {
      warnedCoverageRef.current = true;
      console.warn(
        "[vitrina] The detail panel covers \u226595% of the plane with modal={false}: Tab reaches a plane nobody can see. Pass modal={true} at the same breakpoint where --vitrina-panel-size makes the panel cover everything."
      );
    }
  }, [panelOpen, modal]);
  const activeId = controlledActive ? activeIdProp : detail.panel === "open" ? detail.active?.entityId ?? null : null;
  const api = useMemo3(
    () => ({
      zoomSteps,
      zoomIndex: index,
      zoom,
      zoomIn: () => {
        if (!viewLocked) setZoomIndex((i) => clampIndex(i + 1, stepCount));
      },
      zoomOut: () => {
        if (!viewLocked) setZoomIndex((i) => clampIndex(i - 1, stepCount));
      },
      setZoomIndex: (next) => {
        if (!viewLocked) setZoomIndex(clampIndex(next, stepCount));
      },
      view,
      setView,
      toggleView: () => setView(view === "plane" ? "grid" : "plane"),
      viewLocked,
      activeId,
      detailPhase: detail.panel === "open" ? "open" : detail.panel === "covering" ? "closing" : "idle",
      openDetail,
      closeDetail,
      labels
    }),
    [zoomSteps, index, zoom, stepCount, view, setView, viewLocked, activeId, detail.panel, openDetail, closeDetail, labels]
  );
  const copy = activeCopy(detail);
  const hiddenIds = useMemo3(() => hiddenInstancesOf(detail), [detail]);
  const activeCtxEntityId = detail.panel === "closed" ? null : detail.active?.entityId ?? null;
  const relayEntity = detail.relaying ? entityById.get(detail.relaying.entityId) ?? null : null;
  const panelAnimPhaseRef = useRef3("closed");
  const panelAnimRef = useRef3("none");
  if (panelAnimPhaseRef.current !== detail.panel) {
    const prev = panelAnimPhaseRef.current;
    panelAnimPhaseRef.current = detail.panel;
    if (detail.panel === "covering") panelAnimRef.current = "cover";
    else if (detail.panel === "open" && prev !== "open") panelAnimRef.current = "reveal";
    else if (detail.panel === "closed") panelAnimRef.current = "none";
  }
  if (panelAnimRef.current === "reveal" && detail.panel === "open" && detail.flight !== "waiting") {
    panelAnimRef.current = "none";
  }
  const panelAnim = panelAnimRef.current;
  const relayFlying = relayInstanceId !== null;
  return /* @__PURE__ */ jsx4(
    "div",
    {
      ref: rootRef,
      "data-vitrina-root": "",
      "data-vitrina-view": view,
      "data-vitrina-panel-phase": detail.panel,
      "data-vitrina-reduced": reduced ? "" : void 0,
      className,
      tabIndex: -1,
      style,
      children: /* @__PURE__ */ jsxs3(VitrinaContext.Provider, { value: api, children: [
        view === "plane" ? /* @__PURE__ */ jsx4(
          Plane,
          {
            entities,
            instances,
            layout: resolvedLayout,
            renderObject,
            labels,
            zoom,
            reduced,
            session,
            hiddenIds,
            activeEntityId: activeCtxEntityId,
            onOpen: openDetail,
            onNode: registerNode,
            onDragStart: onPlaneDragStart,
            onGesture: onPlaneGesture,
            motion
          }
        ) : /* @__PURE__ */ jsx4(
          Grid,
          {
            entities,
            instances,
            layout: resolvedLayout,
            renderObject,
            renderCard,
            renderGridHeader,
            labels,
            reduced,
            session,
            hiddenIds,
            activeEntityId: activeCtxEntityId,
            onOpen: openDetail,
            onNode: registerNode,
            motion
          }
        ),
        activeEntity && /* @__PURE__ */ jsx4(
          Detail,
          {
            entity: activeEntity,
            instanceId: detail.active?.instanceId ?? null,
            panel: detail.panel,
            copy,
            panelAnim,
            view,
            side: panelSide,
            besidePlacement,
            modal,
            relayEntity,
            relayInstanceId: detail.relaying?.instanceId ?? null,
            relayFlying,
            portalReady,
            portalTextStyle: portalTextStyleRef.current,
            renderObject,
            renderAbove,
            renderBeside,
            renderDetail,
            renderBelow,
            renderClose,
            labels,
            detailContext,
            panelRef,
            cardRef,
            slotRef,
            flightRef,
            relayRef
          }
        ),
        children
      ] })
    }
  );
}

// src/VitrinaControls.tsx
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function VitrinaControls({ className, style }) {
  const api = useVitrina();
  if (api.viewLocked) return null;
  const atMin = api.zoomIndex <= 0;
  const atMax = api.zoomIndex >= api.zoomSteps.length - 1;
  return /* @__PURE__ */ jsxs4("div", { "data-vitrina-controls": "", className, style, children: [
    /* @__PURE__ */ jsx5("button", { type: "button", "data-vitrina-zoom-out": "", disabled: atMin, onClick: api.zoomOut, children: api.labels.zoomOut }),
    /* @__PURE__ */ jsx5("button", { type: "button", "data-vitrina-zoom-in": "", disabled: atMax, onClick: api.zoomIn, children: api.labels.zoomIn }),
    /* @__PURE__ */ jsx5("button", { type: "button", "data-vitrina-view-toggle": "", onClick: api.toggleView, children: api.view === "plane" ? api.labels.toGrid : api.labels.toPlane })
  ] });
}
export {
  DEFAULT_LAYOUT,
  Vitrina,
  VitrinaControls,
  generateInstances,
  resolveLayout,
  useVitrina
};
//# sourceMappingURL=index.js.map